import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import {
  CaretDownIcon,
  CaretUpIcon,
  CheckIcon,
  LinkExternalIcon,
} from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import styled from 'styled-components';

import AddOnInquiry from 'dashboard/components/AddOnInquiry/AddOnInquiry';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Modal from 'design-system/components/Modal/Modal';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import Table, { TableBody, TableCell, TableRow } from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import AamvaIcon from 'images/dashboard/add-ons/aamva.svg';
import BusinessVerificationIcon from 'images/dashboard/add-ons/business-verification.svg';
import DatabaseIcon from 'images/dashboard/add-ons/database.svg';

// Note: These enums match what is set in the Inquiry Template
enum AddOn {
  Aamva = 'solution_aamva',
  BusinessVerificationEssentials = 'solution_business_verification',
  InternationalDatabase = 'solution_database_non_us',
}

const AddOnMetadata = {
  [AddOn.Aamva]: {
    title: 'AAMVA',
  },
  [AddOn.BusinessVerificationEssentials]: {
    title: 'Business Verification',
  },
  [AddOn.InternationalDatabase]: {
    title: 'Database',
  },
};

const AddOns = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState<AddOn | null>(null);
  const [showInquiry, setShowInquiry] = useState(false);

  // This is used to change the button to "Requested" after the Inquiry has been submitted.
  // The state will not persist after the user navigates away from the page, but the Inquiry has
  // logic to prevent duplicate submissions.
  const [requestedAddOns, setRequestedAddOns] = useState<AddOn[]>([]);

  return (
    <>
      <Modal
        size='lg'
        title={
          selectedAddOn ? `Request Add-on: ${AddOnMetadata[selectedAddOn].title}` : 'Request Add-on'
        }
        open={showInquiry}
        onOpenChange={() => setShowInquiry(!showInquiry)}
      >
        <AddOnInquiryWrapper>
          <AddOnInquiry
            requestedProductName={selectedAddOn || ''}
            onComplete={() => {
              setRequestedAddOns([...requestedAddOns, selectedAddOn!]);
              setShowInquiry(false);
            }}
          />
        </AddOnInquiryWrapper>
      </Modal>
      <CardWrapper>
        <AddOnsTitle>
          Add-ons
          <AddOnsSticker variant='purple'>NEW</AddOnsSticker>
        </AddOnsTitle>
        <Spacer height={1} />
        <Paragraph size='md' color='primary'>
          Add new features to your existing plan, no plan upgrades required.
        </Paragraph>
        <Spacer height={3} />
        <AddOnsWrapper>
          <AddOnOffering>
            <AamvaIcon />
            <Spacer height={2} />
            <AddOnOfferingTitle>AAMVA</AddOnOfferingTitle>
            <AddOnsSticker variant='purple'>$1.50 / run</AddOnsSticker>
            <Spacer height={2} />
            <AddOnOfferingDescription>
              Validate IDs with live DMV data to reduce fraudulent submissions and tampered
              biometric information.
            </AddOnOfferingDescription>
            <Spacer height={2} />
            {isExpanded ? (
              <AddonViewMore onClick={() => setIsExpanded(false)}>
                View less
                <CaretUpIcon />
              </AddonViewMore>
            ) : (
              <AddonViewMore onClick={() => setIsExpanded(true)}>
                View more
                <CaretDownIcon />
              </AddonViewMore>
            )}
            <Spacer height={2} />
            {isExpanded && (
              <>
                <AddOnOferringSubtitle>What you get</AddOnOferringSubtitle>
                <Spacer height={2} />
                <AddOnOfferingRow>
                  <CheckIcon size={24} />
                  AAMVA verification coverage from 40+ issuing state databases
                </AddOnOfferingRow>
                <AddOnOfferingRow>
                  <CheckIcon size={24} />
                  Automatic retry logic that effectively manages AAMVA service issues
                </AddOnOfferingRow>
                <Spacer height={2} />
                <AddOnOfferingLearnMoreLink>
                  <a
                    href='https://help.withpersona.com/articles/76IuZmA7MC9tdemWcSb49M/'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Learn more
                    <LinkExternalIcon />
                  </a>
                </AddOnOfferingLearnMoreLink>
                <Spacer height={2} />
              </>
            )}
            <Spacer height={2} />
            <Button
              data-test='add-ons__aamva-request-button'
              disabled={requestedAddOns.includes(AddOn.Aamva)}
              onClick={() => {
                setSelectedAddOn(AddOn.Aamva);
                setShowInquiry(true);
              }}
            >
              {requestedAddOns.includes(AddOn.Aamva) ? 'Requested' : 'Request'}
            </Button>
          </AddOnOffering>
          <AddOnOffering>
            <DatabaseIcon />
            <Spacer height={2} />
            <AddOnOfferingTitle>Database</AddOnOfferingTitle>
            <AddOnsSticker variant='purple'>$1.50+ / run</AddOnsSticker>
            <Spacer height={2} />
            <AddOnOfferingDescription>
              Verify collected and extracted PII against authoritative global databases and issuing
              sources to maximize coverage.
            </AddOnOfferingDescription>
            <Spacer height={2} />
            {isExpanded ? (
              <AddonViewMore onClick={() => setIsExpanded(false)}>
                View less
                <CaretUpIcon />
              </AddonViewMore>
            ) : (
              <AddonViewMore onClick={() => setIsExpanded(true)}>
                View more
                <CaretDownIcon />
              </AddonViewMore>
            )}
            <Spacer height={2} />
            {isExpanded && (
              <>
                <AddOnOferringSubtitle>Countries included</AddOnOferringSubtitle>
                <Spacer height={2} />
                <AddOnOfferingTable>
                  <TableBody>
                    <TableRow>
                      <TableCell>United Kingdom</TableCell>
                      <TableCell>
                        <AddOnsSticker>$2.40 / run</AddOnsSticker>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Brazil</TableCell>
                      <TableCell>
                        <AddOnsSticker>$1.50 / run</AddOnsSticker>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Mexico</TableCell>
                      <TableCell>
                        <AddOnsSticker>$1.50 / run</AddOnsSticker>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>India</TableCell>
                      <TableCell>
                        <AddOnsSticker>$1.50 / run</AddOnsSticker>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </AddOnOfferingTable>
                <Spacer height={2} />
                <AddOnOfferingLearnMoreLink>
                  <a
                    href='https://help.withpersona.com/verifications/features/verification-service-types/'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Learn more
                    <LinkExternalIcon />
                  </a>
                </AddOnOfferingLearnMoreLink>
                <Spacer height={2} />
              </>
            )}
            <Spacer height={2} />
            <Button
              data-test='add-ons__international-database-request-button'
              disabled={requestedAddOns.includes(AddOn.InternationalDatabase)}
              onClick={() => {
                setSelectedAddOn(AddOn.InternationalDatabase);
                setShowInquiry(true);
              }}
            >
              {requestedAddOns.includes(AddOn.InternationalDatabase) ? 'Requested' : 'Request'}
            </Button>
          </AddOnOffering>
          <AddOnOffering>
            <BusinessVerificationIcon />
            <Spacer height={2} />
            <AddOnOfferingTitle>Business Verification</AddOnOfferingTitle>
            <AddOnsSticker variant='purple'>$1.875 / run</AddOnsSticker>
            <Spacer height={2} />
            <AddOnOfferingDescription>
              Get real-time Know Your Business (US only) with our foundational business
              verifications and reports.
            </AddOnOfferingDescription>
            <Spacer height={2} />
            {isExpanded ? (
              <AddonViewMore onClick={() => setIsExpanded(false)}>
                View less
                <CaretUpIcon />
              </AddonViewMore>
            ) : (
              <AddonViewMore onClick={() => setIsExpanded(true)}>
                View more
                <CaretDownIcon />
              </AddonViewMore>
            )}
            <Spacer height={2} />
            {isExpanded && (
              <>
                <AddOnOferringSubtitle>Services included</AddOnOferringSubtitle>
                <Spacer height={2} />
                <AddOnOfferingTable>
                  <TableBody>
                    <TableRow>
                      <TableCell>TIN (US)</TableCell>
                      <TableCell>
                        <AddOnsSticker>$1.875 / run</AddOnsSticker>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Business Registry Verification</TableCell>
                      <TableCell>
                        <AddOnsSticker>$1.875 / run</AddOnsSticker>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Business Watchlist Report</TableCell>
                      <TableCell>
                        <AddOnsSticker>$1.875 / run</AddOnsSticker>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Business Adverse Media Report</TableCell>
                      <TableCell>
                        <AddOnsSticker>$1.875 / run</AddOnsSticker>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </AddOnOfferingTable>
                <Spacer height={2} />
                <AddOnOfferingLearnMoreLink>
                  <a
                    href='https://help.withpersona.com/articles/4Z9k3Y9n4xlN6jMQpoVrGu/'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Learn more
                    <LinkExternalIcon />
                  </a>
                </AddOnOfferingLearnMoreLink>
                <Spacer height={2} />
              </>
            )}
            <Spacer height={2} />
            <Button
              data-test='add-ons__business-verification-essentials-request-button'
              disabled={requestedAddOns.includes(AddOn.BusinessVerificationEssentials)}
              onClick={() => {
                setSelectedAddOn(AddOn.BusinessVerificationEssentials);
                setShowInquiry(true);
              }}
            >
              {requestedAddOns.includes(AddOn.BusinessVerificationEssentials)
                ? 'Requested'
                : 'Request'}
            </Button>
          </AddOnOffering>
        </AddOnsWrapper>
      </CardWrapper>
    </>
  );
};

const CardWrapper = styled(Card)`
  padding: ${Spacing[4]} ${Spacing[6]};
`;

const AddOnsTitle = styled.div`
  ${Typography.TextLargeBold};
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};
`;

const AddOnsWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const AddOnOffering = styled.div`
  padding: ${Spacing[4]} ${Spacing[6]};
  border-right: 1px solid ${Color.BorderHairline};
  min-width: 200px;

  &:last-of-type {
    border: none;
  }
`;

const AddOnOfferingTitle = styled.div`
  ${Typography.HeadingSmall};
`;

const AddOnOfferingDescription = styled.div`
  color: ${Color.TextSecondary};
`;

const AddonViewMore = styled.div`
  ${Typography.TextSmall};
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[1]};
  color: ${Color.CorePurple700};
  cursor: pointer;
`;

const AddOnOferringSubtitle = styled.div`
  ${Typography.TextMediumBold};
`;

const AddOnOfferingRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[3]};
  margin-bottom: ${Spacing[4]};
  color: ${Color.TextSecondary};
`;

const AddOnOfferingTable = styled(Table)`
  td {
    color: ${Color.TextSecondary};
    border: none;
    padding: ${Spacing[1]};
  }
`;

const AddOnOfferingLearnMoreLink = styled.div`
  a {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: ${Spacing[1]};
  }
`;

const AddOnsSticker = styled(Sticker)`
  width: auto;
  font-size: 10px;
`;

const AddOnInquiryWrapper = styled.div`
  margin-top: ${Spacing[4]};
  height: 600px;
  display: block;

  & > iframe {
    width: 100%;
    height: 100%;
  }
`;

export default AddOns;
